import { Box, Chip, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material'
import { Replay as ReplayIcon, StopCircle as StopIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { CategoryStatus, RunState } from '../../types/RunStatus'
import { CheckCategory } from '../../types/ReleaseCheckItem'

export const isRunningState = (state: RunState | undefined) =>
  state === 'Pending' || state === 'Running' || state === 'ContinuedAsNew'

interface CategoryRowProps {
  category: CheckCategory
  status: CategoryStatus | undefined
  umbrellaRunning: boolean
  onRescan: (category: CheckCategory) => void
  onForceRestart: (category: CheckCategory) => void
  isStarting: boolean
  startingCategory: CheckCategory | undefined
}

const CategoryRow = ({
  category,
  status,
  umbrellaRunning,
  onRescan,
  onForceRestart,
  isStarting,
  startingCategory,
}: CategoryRowProps) => {
  const { t } = useTranslation()
  const state = status?.state ?? 'Idle'
  const running = isRunningState(state)
  const custom = status?.customStatus
  const total = custom?.totalItems ?? 0
  const completed = custom?.completedItems ?? 0
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0

  // Disable our own button when: anything umbrella is in flight, this category is in flight,
  // or this category is the one currently being submitted.
  const buttonDisabled =
    umbrellaRunning || running || (isStarting && startingCategory === category)

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box sx={{ minWidth: 160 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="subtitle2">
            {t(`releaseCheck.category.${category}`, category)}
          </Typography>
          <Tooltip
            title={
              umbrellaRunning
                ? t('releaseCheck.tooltip.allRunning', 'Full scan in progress')
                : t('releaseCheck.tooltip.rescanCategory', 'Re-scan this category')
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={buttonDisabled}
                onClick={() => onRescan(category)}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Chip
          size="small"
          label={t(`releaseCheck.state.${state}`, state)}
          color={running ? 'warning' : state === 'Failed' ? 'error' : 'default'}
          sx={{ mt: 0.5 }}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        {running ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {custom?.phase
                ? t(`releaseCheck.phase.${custom.phase}`, custom.phase)
                : t('releaseCheck.phase.Running', 'Running')}
              {total > 0 ? ` — ${completed} / ${total}` : ''}
            </Typography>
            <LinearProgress
              variant={total > 0 ? 'determinate' : 'indeterminate'}
              value={percent}
              sx={{ mt: 0.5 }}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Box>
      {running && !umbrellaRunning && (
        <Tooltip title={t('releaseCheck.tooltip.forceRestartCategory', 'Stop and re-scan this category')}>
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => onForceRestart(category)}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}

export default CategoryRow
